/**
 * SMMS Agentic Operations — app shell (ADR-0001 Option C: separate
 * tenant-facing deployable, its own nav and routes; shares identity, API,
 * and design system with the core web app).
 */
import { Navigate, Route, Routes } from 'react-router';
import { Spinner } from '@smms/design-system';
import { useAuth } from './auth/context';
import AgenticNav from './components/AgenticNav';
import { SafeModeBanner } from './components/safeMode';
import LoginPage from './pages/LoginPage';
import OverviewPage from './pages/OverviewPage';
import DecisionInboxPage from './pages/DecisionInboxPage';
import DecisionDetailPage from './pages/DecisionDetailPage';
import AgentsPage from './pages/AgentsPage';
import UsagePage from './pages/UsagePage';
import ExceptionsPage from './pages/ExceptionsPage';
import PoliciesPage from './pages/PoliciesPage';
import AgentDetailPage from './pages/AgentDetailPage';
import RunsPage from './pages/RunsPage';
import ConfigApprovalsPage from './pages/ConfigApprovalsPage';
import KnowledgeHealthPage from './pages/KnowledgeHealthPage';

export default function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <Spinner size="lg" label="Loading" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <div className="flex min-h-screen bg-bg">
      {/* Skip-to-content link (WCAG 2.4.1) — same pattern as
          frontend/src/features/dashboard/components/DashboardV2Layout.tsx:
          visually hidden until keyboard-focused, so a keyboard/SR user can
          bypass AgenticNav's persistent sidebar. Lane 4
          (followups/lane4-a11y-sweep, 2026-09-01): agentic/ shipped with no
          skip link at all — the one real gap `a11y-agentic.spec.ts`'s
          keyboard-reachability pass surfaced. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-on-primary focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        Skip to main content
      </a>
      <AgenticNav />
      <main id="main-content" tabIndex={-1} className="min-w-0 flex-1 focus:outline-none">
        {/* P12-AG03: the brake state is shell-level so EVERY page reflects the
            real server truth (GET /agentic/safe-mode), not just Overview and
            Policies. Renders only while Safe Mode is active. */}
        <SafeModeBanner />
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/decisions" element={<DecisionInboxPage />} />
          <Route path="/decisions/:id" element={<DecisionDetailPage />} />
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/agents/:agentId" element={<AgentDetailPage />} />
          <Route path="/runs" element={<RunsPage />} />
          <Route path="/knowledge" element={<KnowledgeHealthPage />} />
          <Route path="/approvals" element={<ConfigApprovalsPage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/exceptions" element={<ExceptionsPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
